import ClearFilter from './templates/ClearFilter';
import SearchFilter from './templates/SearchFilter';
import { CaretDownOutlined, CheckOutlined } from '@vindr/icons';
import { Row, Select } from 'antd';
import { isEqual } from 'lodash';
import { Suspense, memo } from 'react';
import { Updater, useImmer } from 'use-immer';

const { Option } = Select;

export interface FilterList {
  label: string;
  hidden: boolean;
  key: string | string[];
  component: JSX.Element;
}

export interface FilterGroupProps {
  filterList: {
    [key: string]: FilterList;
  };
  showList: Array<string>;
  hiddenList: Array<string>;
  onSearch: () => void;
  onClear: () => void;
  setFilter: Updater<any>;
}

export type useFilterItemsType = [
  FilterGroupProps['filterList'],
  FilterGroupProps['hiddenList'],
  FilterGroupProps['showList']
];

const FilterGroup = ({
  filterList,
  showList,
  hiddenList,
  onClear,
  onSearch,
  setFilter,
}: FilterGroupProps) => {
  const [searchFields, setSearchFields] = useImmer(showList);
  const [options, setOptions] = useImmer(hiddenList);

  const handleChangeSearchKey = (selected: string, newSelected: string) => {
    if (selected === newSelected) return;
    const index = searchFields.indexOf(selected);

    // Clean value of old key
    setFilter((draft) => {
      if (typeof filterList[selected].key === 'string') {
        delete draft[filterList[selected].key as string];
      } else {
        (filterList[selected].key as string[]).forEach((key) => {
          delete draft[key];
        });
      }
    });

    setSearchFields((draft) => {
      draft[index] = newSelected;
    });

    setOptions((draft) => {
      draft.push(selected);
      const index = draft.indexOf(newSelected);
      draft.splice(index, 1);
    });
  };

  return (
    <Row className="ml-[-7px] space-y-2 pt-3 pb-6" align="bottom">
      {searchFields &&
        filterList &&
        searchFields.map((key: string) => {
          const field = filterList[key];
          const Component = field.component;

          return (
            <div key={key} className="search-item">
              <Row className="mr-1">
                <Select
                  size="small"
                  bordered={false}
                  menuItemSelectedIcon={<CheckOutlined />}
                  suffixIcon={<CaretDownOutlined />}
                  value={field.label}
                  dropdownMatchSelectWidth={false}
                  onChange={(value) => handleChangeSearchKey(key, value)}
                >
                  {options.map((key: string | number) => {
                    const field = filterList[key];
                    return (
                      <Option key={field.label} value={key}>
                        {field.label}
                      </Option>
                    );
                  })}
                </Select>
              </Row>
              <Row className="pl-[7px]">
                <Suspense fallback={<div className="w-32" />}>{Component}</Suspense>
              </Row>
            </div>
          );
        })}

      <div className="ml-[7px] flex flex-wrap space-x-2">
        <SearchFilter onClick={onSearch} />

        <ClearFilter onClick={onClear} />
      </div>
    </Row>
  );
};

export const GenerateListFilter = (
  filterList: FilterGroupProps['filterList']
): [FilterGroupProps['hiddenList'], FilterGroupProps['showList']] => {
  const hiddenList = [];
  const showList = [];

  Object.entries(filterList).forEach(([key, value]) => {
    if (value.hidden) hiddenList.push(key);
    else showList.push(key);
  });

  return [hiddenList, showList];
};

export default memo(FilterGroup, (prevProps, nextProps) => {
  return isEqual(prevProps.filterList, nextProps.filterList);
});
